/**
 * The landing state of User Management, instead of an empty search box.
 *
 * There is no list endpoint — upstream only answers "tell me about this one
 * email" — so this deliberately shows what is genuinely known rather than
 * inventing counts: the operator's own recent lookups, the actions they can
 * start from cold, and what the provider colours mean.
 *
 * Recent lookups live in this browser only (localStorage): they are one
 * operator's trail, not shared state, and they must never become a place where
 * customer emails are stored server-side.
 */
import { Link } from "react-router-dom";
import { Clock, CreditCard, UserPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProviderBadge } from "./provider-badge";
import { Card } from "./parts";
import { formatDate } from "./format";
import type { RecentLookup } from "./recent-lookups";

export function UsersHome({
  recent,
  onPick,
  onClear,
}: {
  recent: RecentLookup[];
  onPick: (email: string) => void;
  onClear: () => void;
}) {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card
        title="Recent lookups"
        description="On this browser only"
        className="lg:col-span-2"
        actions={
          recent.length > 0 && (
            <Button variant="ghost" size="sm" onClick={onClear}>
              <X className="mr-1 h-3 w-3" />
              Clear
            </Button>
          )
        }
      >
        {recent.length === 0 ? (
          <p className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">
            Search a user's email above to get started. The ones you look at
            will appear here.
          </p>
        ) : (
          <ul className="divide-y">
            {recent.map((r) => (
              <li key={r.email}>
                <button
                  type="button"
                  onClick={() => onPick(r.email)}
                  className="flex w-full items-center justify-between gap-3 px-1 py-2.5 text-left text-sm hover:text-primary"
                >
                  <span className="truncate font-medium">{r.email}</span>
                  <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {formatDate(r.at, true)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="space-y-4">
        <Card title="Start here">
          <div className="space-y-2">
            <Button variant="outline" className="w-full justify-start" asChild>
              <Link to="/dashboard/users/new">
                <UserPlus className="mr-2 h-4 w-4" />
                Create a user
              </Link>
            </Button>
            <p className="text-xs text-muted-foreground">
              Everything else — plans, add-ons, email, password, billing — works
              on a user you have looked up.
            </p>
          </div>
        </Card>

        <Card title="Payment providers" description="What the colours mean">
          <div className="flex flex-wrap gap-2">
            <ProviderBadge provider="fastspring" />
            <ProviderBadge provider="stripe" />
            <ProviderBadge provider="bluesnap" />
          </div>
          <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
            <CreditCard className="mt-0.5 h-3 w-3 shrink-0" />
            BlueSnap is the legacy provider — its subscriptions show, but we
            cannot query it live.
          </p>
        </Card>
      </div>
    </div>
  );
}
